/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { AxiosError } from 'axios';
import axiosConfig, { ApiRequestType } from 'axiosConfig/axiosConfig';
import { WorkspaceObject } from 'jotai/workspace';
import {
  SanctioningBodyEnum,
  Workspace,
  WorkspaceInfo,
  WorkspaceRequest,
  WorkspaceResponseStatus,
} from 'models/viewModels';

class UserWorkspaceApi {
  async getWorkspace(id: string): Promise<Workspace> {
    const response = new Promise<Workspace>((resolve, reject) => {
      axiosConfig(ApiRequestType.MotorsportsCompToolsApi)
        .get('/rest/user/workspace', { params: { id: id } })
        .then((res) => {
          resolve(res.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
    return response;
  }

  async updateWorkspace(
    workspace: WorkspaceObject,
    alternativeEnvironment?: boolean
  ): Promise<WorkspaceResponseStatus> {
    const request = {
      id: workspace.id,
      shared: workspace.shared || false,
      data: JSON.stringify(workspace),
      sharedDomainsList: workspace.sharedDomains || [],
    } as WorkspaceRequest;
    const response = new Promise<WorkspaceResponseStatus>((resolve, reject) => {
      alternativeEnvironment
        ? axiosConfig(ApiRequestType.AlternativeEnvironmentApi)
            .post('/rest/user/workspaces', request)
            .then((res) => {
              resolve(res.data);
            })
            .catch((error) => {
              reject(error);
            })
        : axiosConfig(ApiRequestType.MotorsportsCompToolsApi)
            .post('/rest/user/workspaces', request)
            .then((res) => {
              resolve(res.data);
            })
            .catch((error) => {
              reject(error);
            });
    });
    return response;
  }

  async deleteWorkspace(id: string): Promise<WorkspaceResponseStatus> {
    const response = new Promise<WorkspaceResponseStatus>((resolve, reject) => {
      axiosConfig(ApiRequestType.MotorsportsCompToolsApi)
        .post('/rest/user/deleteWorkspace', null, { params: { id: id } })
        .then((res) => resolve(res.data))
        .catch((error: AxiosError) => reject(new AxiosError(error.message, error.code)));
    });
    return response;
  }

  async getAllWorkspaces(): Promise<Workspace[]> {
    const response = new Promise<Workspace[]>((resolve, reject) => {
      axiosConfig(ApiRequestType.MotorsportsCompToolsApi)
        .get('/rest/user/workspaces')
        .then((res) => {
          return resolve(res.data);
        })
        .catch((error: AxiosError) => reject(new AxiosError(error.message, error.code)));
    });
    return response;
  }
  async getAvailableWorkspacesBySanctioningBody(sanctioningBody: SanctioningBodyEnum): Promise<Workspace[]> {
    const response = new Promise<Workspace[]>((resolve, reject) => {
      axiosConfig(ApiRequestType.MotorsportsCompToolsApi)
        .get('/rest/user/sanctioningBodyWorkspaces', {
          params: { sanctioningBody: sanctioningBody },
        })
        .then((res) => {
          resolve(res.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
    return response;
  }

  async getAllWorkspaceInfo(): Promise<WorkspaceInfo[]> {
    const response = new Promise<WorkspaceInfo[]>((resolve, reject) => {
      axiosConfig(ApiRequestType.MotorsportsCompToolsApi)
        .get('/rest/user/allWorkspaceInfo')
        .then((res) => {
          resolve(res.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
    return response;
  }
}

export const userWorkspaceApi = new UserWorkspaceApi();
